import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Badge, Box, Icon, IconButton, Spinner, Stack, Text, Tooltip } from "@chakra-ui/react";
import { MdOpenInNew, MdPictureAsPdf } from "react-icons/md";
import {
    INVOICE_STATUS_LABELS,
    INVOICE_STATUS_COLORS,
    TIER_LABELS,
    TIER_COLORS,
    DEFAULT_CHIP_COLOR,
} from "./subscriptionsConstants";

const rowProps = {
    p: 2.5,
    borderRadius: "6px",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    background: "rgba(255, 255, 255, 0.02)",
};

const tierBadgeProps = (scheme) => ({
    background: scheme.bg,
    color: scheme.color,
    border: `1px solid ${scheme.border}`,
    borderRadius: "999px",
    padding: "1px 8px",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    lineHeight: 1.6,
});

const ZERO_DECIMAL_CURRENCIES = new Set(["mga", "jpy", "krw", "vnd", "clp", "isk", "huf"]);

/**
 * Normalizes the aggregated invoices payload to a plain array.
 * Accepts raw arrays as well as { invoices } / { items } envelopes.
 * @param {*} data - Decrypted API payload
 * @returns {Array}
 */
function normalizeUserInvoices(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.invoices)) return data.invoices;
    if (Array.isArray(data?.items)) return data.items;
    return [];
}

/**
 * Formats a date value coming as ISO string or unix timestamp into DD/MM/YYYY.
 * @param {string|number|null|undefined} value - Date value
 * @returns {string}
 */
function formatInvoiceDate(value) {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "number") {
        return dayjs.unix(value).format("DD/MM/YYYY");
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
}

/**
 * Formats an amount already expressed in the main currency unit (no /100).
 * Integers are kept as-is for zero-decimal currencies (MGA, JPY, ...).
 * @param {number|null|undefined} amount - Amount in main currency unit
 * @param {string} currency - ISO 4217 currency code
 * @returns {string}
 */
function formatAggregatedAmount(amount, currency) {
    if (amount === null || amount === undefined) return "—";
    const code = (currency || "").toLowerCase();
    const num = Number(amount);
    const text = ZERO_DECIMAL_CURRENCIES.has(code) ? String(Math.round(num)) : num.toFixed(2);
    return `${text} ${code.toUpperCase()}`.trim();
}

/**
 * Fetches all aggregated invoices for a user via the admin endpoint.
 * Pushes results through setters; bails out silently if the request was aborted.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchUserInvoices({ userId, signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get(`/admin/subscriptions/user/${userId}/invoices`);
        if (!signal.aborted) setItems(normalizeUserInvoices(data));
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible de charger les factures");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook returning the aggregated invoice list state for a given user.
 * Re-fetches on userId change and aborts in-flight requests on cleanup.
 * @param {number|string|null} userId - Target user ID
 * @returns {{ items: Array, loading: boolean, error: string|null }}
 */
function useUserInvoices(userId) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId) { setItems([]); return undefined; }
        const controller = new AbortController();
        fetchUserInvoices({ userId, signal: controller.signal, setItems, setError, setLoading });
        return () => controller.abort();
    }, [userId]);

    return { items, loading, error };
}

/**
 * Renders the colored status chip for an invoice.
 * @param {Object} props
 * @param {string} props.status - Stripe invoice status key
 * @returns {React.ReactElement}
 */
function InvoiceStatusChip({ status }) {
    const scheme = INVOICE_STATUS_COLORS[status] || DEFAULT_CHIP_COLOR;
    const label = INVOICE_STATUS_LABELS[status] || status || "—";
    return (
        <Badge background={scheme.bg} color={scheme.color} border={`1px solid ${scheme.border}`}
            fontWeight={600} fontSize="0.68rem" px={2} borderRadius="full" textTransform="none">
            {label}
        </Badge>
    );
}

/**
 * Renders the subscription tier badge (Blue/Violet/Silver Star/VIP).
 * @param {Object} props
 * @param {string} props.type - Subscription tier key
 * @returns {React.ReactElement}
 */
function TierBadge({ type }) {
    const scheme = TIER_COLORS[type] || DEFAULT_CHIP_COLOR;
    const label = TIER_LABELS[type] || type || "—";
    return <Box as="span" {...tierBadgeProps(scheme)}>{label}</Box>;
}

/**
 * Renders the open-in-Stripe and download-PDF icon buttons for an invoice.
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object
 * @returns {React.ReactElement}
 */
function InvoiceActions({ invoice }) {
    return (
        <Stack direction="row" spacing={1}>
            {invoice?.hosted_invoice_url && (
                <Tooltip label="Ouvrir la facture Stripe" hasArrow>
                    <IconButton as="a" aria-label="Ouvrir la facture Stripe" size="sm" variant="ghost"
                        href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                        color="rgba(255, 255, 255, 0.7)" icon={<Icon as={MdOpenInNew} boxSize="16px" />} />
                </Tooltip>
            )}
            {invoice?.invoice_pdf && (
                <Tooltip label="Télécharger le PDF" hasArrow>
                    <IconButton as="a" aria-label="Télécharger le PDF" size="sm" variant="ghost"
                        href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer"
                        color="rgba(255, 255, 255, 0.7)" icon={<Icon as={MdPictureAsPdf} boxSize="16px" />} />
                </Tooltip>
            )}
        </Stack>
    );
}

/**
 * Renders a single invoice row with tier badge, ID, date, amount, status and actions.
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object
 * @returns {React.ReactElement}
 */
function InvoiceRow({ invoice }) {
    const amount = invoice?.amount_paid != null ? invoice.amount_paid : invoice?.amount_due;
    return (
        <Box {...rowProps}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Stack spacing={1} minW={0} flex={1}>
                    <Stack direction="row" spacing={1.5} alignItems="center" minW={0}>
                        <TierBadge type={invoice?.subscription_type} />
                        <Text noOfLines={1} color="rgba(255, 255, 255, 0.95)" fontSize="0.78rem" fontWeight={600} fontFamily="monospace">
                            {invoice?.id || "—"}
                        </Text>
                    </Stack>
                    <Text color="rgba(255, 255, 255, 0.55)" fontSize="0.7rem">
                        {formatInvoiceDate(invoice?.created)} · {formatAggregatedAmount(amount, invoice?.currency)}
                    </Text>
                </Stack>
                <InvoiceStatusChip status={invoice?.status} />
                <InvoiceActions invoice={invoice} />
            </Stack>
        </Box>
    );
}

/**
 * Aggregated invoices list for a user. Single GET to the admin endpoint,
 * already sorted by created DESC server-side; no client-side pagination.
 * @param {Object} props
 * @param {number|string} props.userId - Target user ID
 * @returns {React.ReactElement}
 */
function UserInvoicesList({ userId }) {
    const { items, loading, error } = useUserInvoices(userId);

    if (loading && items.length === 0) {
        return <Box display="flex" justifyContent="center" py={3}><Spinner size="md" color="#17dcb8" /></Box>;
    }
    if (error) return <Text color="rgba(229, 115, 115, 1)" fontSize="0.85rem">{typeof error === "string" ? error : "Erreur"}</Text>;
    if (items.length === 0) return <Text color="rgba(255, 255, 255, 0.5)" fontSize="0.85rem">Aucune facture</Text>;

    return (
        <Stack spacing={2}>
            {items.map((inv) => <InvoiceRow key={inv?.id} invoice={inv} />)}
        </Stack>
    );
}

export default UserInvoicesList;
